import { useCallback, useMemo, useState } from 'react';

import {
  buildDefaultCustomizations,
  findOptionBySelection,
  findOptionBySku,
  isSkinlessSelected,
  isSmokeTurmericOption,
  optionKey,
  selectionFromStoredCustomizations,
} from './productCustomizationHelpers';
import { resolveCustomizationsForCart } from '../../services/commerce';

/**
 * Shared customization selection state + skinless / Smoke & Turmeric rules.
 * Used by both the product page and the cart so the two flows never diverge.
 */
export function useCustomizationSelection(product) {
  const [selected, setSelected] = useState({});

  const resetToDefaults = useCallback(() => {
    setSelected(buildDefaultCustomizations(product));
  }, [product]);

  const resetToStored = useCallback(
    (storedCustomizations) => {
      setSelected(selectionFromStoredCustomizations(product, storedCustomizations));
    },
    [product]
  );

  const selectSingle = useCallback(
    (groupKey, option) => {
      const selectedOptionId = optionKey(option);
      if (!selectedOptionId) return;
      setSelected((prev) => {
        // Guard: don't allow Smoke & Turmeric when Skinless is selected.
        if (
          groupKey === 'flavour' &&
          isSmokeTurmericOption(option) &&
          isSkinlessSelected(prev, product?.customizations)
        ) {
          return prev;
        }

        const next = { ...prev, [groupKey]: selectedOptionId };

        // If Type of Meat switches to Skinless, remove incompatible flavour.
        if (groupKey === 'type_of_meat' && isSkinlessSelected(next, product?.customizations)) {
          const flavourConfig = product?.customizations?.flavour;
          const selectedFlavour = findOptionBySelection(flavourConfig, next.flavour);
          if (isSmokeTurmericOption(selectedFlavour)) {
            const normalOption = findOptionBySku(flavourConfig, 'NML');
            if (normalOption) next.flavour = optionKey(normalOption);
            else delete next.flavour;
          }
        }

        return next;
      });
    },
    [product]
  );

  const toggleCheckbox = useCallback((groupKey, option, checked) => {
    const id = optionKey(option);
    if (!id) return;
    setSelected((prev) => {
      const current = Array.isArray(prev[groupKey]) ? prev[groupKey] : [];
      const next = checked
        ? [...new Set([...current, id])]
        : current.filter((x) => x !== id);
      return { ...prev, [groupKey]: next };
    });
  }, []);

  const resolved = useMemo(
    () => resolveCustomizationsForCart(product?.customizations, selected),
    [product?.customizations, selected]
  );

  return {
    selected,
    setSelected,
    resetToDefaults,
    resetToStored,
    selectSingle,
    toggleCheckbox,
    resolved,
  };
}
